import { CommonModule, CurrencyPipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, LOCALE_ID, Output } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductData } from "@domain/project/product-data/product-data";
import {
  getAdditionalProductsTableDraftQuantity,
  getAdditionalProductsTableProductQuantity,
} from "@domain/project/product-list/additional-products-display.util";
import { ProductListDiscountKey } from "@domain/project/product-list/product-list-discount-key.util";
import {
  calculateDiscountedTotal,
  formatProductListDiscount,
  getProductListPriceData,
  isProductListDiscountEditable,
  normalizeProductListDiscountDisplay,
} from "@domain/project/product-list/product-list-discount.util";
import { AdditionalConfigDisplayData } from "@domain/project/product-list/product-list-display-data.util";

@Component({
  selector: "app-additional-products-table",
  imports: [CommonModule, CurrencyPipe, UiKitModule],
  templateUrl: "./additional-products-table.component.html",
  styleUrl: "./additional-products-table.component.scss",
  standalone: true,
})
export class AdditionalProductsTableComponent {
  private readonly locale = inject(LOCALE_ID);

  @Input({ required: true }) configs: AdditionalConfigDisplayData<ProductData>[] = [];
  @Input() editMode = false;
  @Input() draftQuantities: Record<string, number> = {};
  @Input() emptyMessage = "";
  @Input() showCosts = false;
  @Input() showZrefPrices = false;
  @Input({ required: true }) currencyCode!: string;

  @Output() quantityChange = new EventEmitter<{ configId: string; value: number | string }>();
  @Output() quantityStep = new EventEmitter<{ configId: string; delta: number }>();
  @Output() remove = new EventEmitter<string>();
  @Output() discountChange = new EventEmitter<{ key: ProductListDiscountKey; value: string }>();

  protected readonly tooltipTextWarningSign: string = $localize`:@@productlist.table.warningSign.tooltip:Produkt enthält abgekündigte Komponenten oder ist Teil einer unvollständigen Konfiguration`;

  protected getDraftQuantity(config: AdditionalConfigDisplayData<ProductData>) {
    return getAdditionalProductsTableDraftQuantity(this.editMode, this.draftQuantities, {
      config: {
        id: config.configId,
        additionalProductListQuantity: config.savedQuantity,
      },
    });
  }

  protected getProductQuantity(product: ProductData, config: AdditionalConfigDisplayData<ProductData>) {
    return getAdditionalProductsTableProductQuantity(this.editMode, this.draftQuantities, product, {
      config: {
        id: config.configId,
        additionalProductListQuantity: config.savedQuantity,
      },
    });
  }

  protected updateAdditionalProductQuantity(configId: string, delta: number) {
    this.quantityStep.emit({ configId, delta });
  }

  protected setAdditionalProductQuantity(configId: string, value: number | string) {
    this.quantityChange.emit({ configId, value });
  }

  protected onQuantityInput(event: Event, configId: string): void {
    if (event.target instanceof HTMLInputElement) {
      this.setAdditionalProductQuantity(configId, event.target.value);
    }
  }

  protected removeAdditionalProduct(configId: string) {
    this.remove.emit(configId);
  }

  protected getProductTotal(product: ProductData, config: AdditionalConfigDisplayData<ProductData>) {
    const priceData = getProductListPriceData(product, this.showZrefPrices);
    return priceData
      ? calculateDiscountedTotal(priceData.price, this.getDraftQuantity(config) * product.quantity, product.discount ?? 0)
      : 0;
  }

  protected hasEditablePrice(product: ProductData): boolean {
    return isProductListDiscountEditable(
      this.editMode,
      this.showCosts,
      getProductListPriceData(product, this.showZrefPrices),
      !!product.discountKey,
    );
  }

  protected hasPrice(product: ProductData): boolean {
    return !!getProductListPriceData(product, this.showZrefPrices);
  }

  protected formatDiscount(discount: number): string {
    return formatProductListDiscount(discount, this.locale);
  }

  protected normalizeDiscountInput(input: HTMLInputElement, product: ProductData): void {
    const normalizedDiscount = normalizeProductListDiscountDisplay(input.value, product.discount ?? 0, this.locale);
    input.value = normalizedDiscount.formattedValue;
    if (normalizedDiscount.discount !== undefined && product.discountKey) {
      this.discountChange.emit({ key: product.discountKey, value: String(normalizedDiscount.discount) });
    }
  }

  protected onDiscountBlur(event: FocusEvent, product: ProductData): void {
    if (event.target instanceof HTMLInputElement) {
      this.normalizeDiscountInput(event.target, product);
    }
  }
}
